/* eslint-disable unicorn/prefer-module */
/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Header, Loader } from "..";
import {
  getEdificiosOperativos,
  getIpsosData,
  procesarIpsosAutomotriz,
  calcularMercadoAutomotriz,
  useCategoriaMoneda,
} from "../../utils";
import styles from "./CategoriaAutomotriz.module.scss";

// Opciones de precios por gama
const PRECIOS_GAMA_ALTA = {
  okm: [40000, 50000, 60000],
  usado: [20000, 25000, 30000],
};
const PRECIOS_GAMA_MEDIA = {
  okm: [15000, 20000, 25000],
  usado: [8000, 10000, 12000],
};

export const Automotriz = () => {
  const { formatNumber, formatUSD, pais } = useCategoriaMoneda();

  const [edificios, setEdificios] = useState([]);
  const [ipsosData, setIpsosData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});

  // Estados de precios (editables)
  const [precioGamaAltaOkm, setPrecioGamaAltaOkm] = useState(50000);
  const [precioGamaAltaUsado, setPrecioGamaAltaUsado] = useState(25000);
  const [precioGamaMediaOkm, setPrecioGamaMediaOkm] = useState(20000);
  const [precioGamaMediaUsado, setPrecioGamaMediaUsado] = useState(10000);

  // Filtros de análisis (todos desmarcados = mostrar todo sin blur)
  const [filtroOkmGamaAlta, setFiltroOkmGamaAlta] = useState(false);
  const [filtroOkmGamaMedia, setFiltroOkmGamaMedia] = useState(false);
  const [filtroUsadoGamaAlta, setFiltroUsadoGamaAlta] = useState(false);
  const [filtroUsadoGamaMedia, setFiltroUsadoGamaMedia] = useState(false);
  const [verMensual, setVerMensual] = useState(false);
  const [showInventarioModal, setShowInventarioModal] = useState(false);

  // Si ninguno está seleccionado, no aplicar blur a nada
  const ningunFiltro = !filtroOkmGamaAlta && !filtroOkmGamaMedia && !filtroUsadoGamaAlta && !filtroUsadoGamaMedia;

  // Función para determinar si aplicar blur a una celda
  const getBlurClass = (tipo, gama) => {
    if (ningunFiltro) return "";
    if (tipo === "okm" && gama === "alta" && filtroOkmGamaAlta) return "";
    if (tipo === "okm" && gama === "media" && filtroOkmGamaMedia) return "";
    if (tipo === "usado" && gama === "alta" && filtroUsadoGamaAlta) return "";
    if (tipo === "usado" && gama === "media" && filtroUsadoGamaMedia) return "";
    // Para filas de header (frecuencia, interpretación) y totales
    if (tipo === "header" && gama === "alta" && (filtroOkmGamaAlta || filtroUsadoGamaAlta)) return "";
    if (tipo === "header" && gama === "media" && (filtroOkmGamaMedia || filtroUsadoGamaMedia)) return "";
    if (tipo === "total") return ""; // Totales siempre visibles
    return styles.celdaBlur;
  };

  // Calcular totales filtrados
  const calcularTotalFiltrado = (resultadosData) => {
    if (!resultadosData) return { gamaAlta: 0, gamaMedia: 0, total: 0 };

    let gamaAltaTotal = 0;
    let gamaMediaTotal = 0;

    if (ningunFiltro) {
      // Mostrar todo
      gamaAltaTotal = resultadosData.mercado.gamaAlta.total;
      gamaMediaTotal = resultadosData.mercado.gamaMedia.total;
    } else {
      // Solo sumar lo seleccionado
      if (filtroOkmGamaAlta) gamaAltaTotal += resultadosData.mercado.gamaAlta.okm;
      if (filtroUsadoGamaAlta) gamaAltaTotal += resultadosData.mercado.gamaAlta.usado;
      if (filtroOkmGamaMedia) gamaMediaTotal += resultadosData.mercado.gamaMedia.okm;
      if (filtroUsadoGamaMedia) gamaMediaTotal += resultadosData.mercado.gamaMedia.usado;
    }

    return {
      gamaAlta: gamaAltaTotal,
      gamaMedia: gamaMediaTotal,
      total: gamaAltaTotal + gamaMediaTotal,
    };
  };

  // Calcular vehículos según filtros (anual o mensual)
  const calcularVehiculosFiltrados = (resultadosData) => {
    if (!resultadosData) return 0;

    let total = 0;

    if (ningunFiltro) {
      // Sumar todas las unidades
      total =
        resultadosData.gamaAlta.okm.anual +
        resultadosData.gamaAlta.usado.anual +
        resultadosData.gamaMedia.okm.anual +
        resultadosData.gamaMedia.usado.anual;
    } else {
      // Solo sumar lo seleccionado
      if (filtroOkmGamaAlta) total += resultadosData.gamaAlta.okm.anual;
      if (filtroUsadoGamaAlta) total += resultadosData.gamaAlta.usado.anual;
      if (filtroOkmGamaMedia) total += resultadosData.gamaMedia.okm.anual;
      if (filtroUsadoGamaMedia) total += resultadosData.gamaMedia.usado.anual;
    }

    // Si es mensual, dividir por 12
    return verMensual ? total / 12 : total;
  };

  // Generar texto dinámico para el resumen ejecutivo
  const generarTextoResumen = () => {
    // Determinar tipo de vehículo
    const tieneOkm = filtroOkmGamaAlta || filtroOkmGamaMedia;
    const tieneUsado = filtroUsadoGamaAlta || filtroUsadoGamaMedia;

    // Determinar gama
    const tieneGamaAlta = filtroOkmGamaAlta || filtroUsadoGamaAlta;
    const tieneGamaMedia = filtroOkmGamaMedia || filtroUsadoGamaMedia;

    // Construir descripción del tipo (separado para poder resaltar)
    const tipoBase = "VEHÍCULOS";
    let tipoSufijo = "";
    let resaltarTipo = false;

    if (!ningunFiltro) {
      if (tieneOkm && !tieneUsado) {
        tipoSufijo = "0KM";
        resaltarTipo = true;
      } else if (tieneUsado && !tieneOkm) {
        tipoSufijo = "USADOS";
        resaltarTipo = true;
      }
    }

    // Construir descripción de gama
    let gama = "";
    let resaltarGama = false;
    if (!ningunFiltro) {
      if (tieneGamaAlta && !tieneGamaMedia) {
        gama = "GAMA ALTA";
        resaltarGama = true;
      } else if (tieneGamaMedia && !tieneGamaAlta) {
        gama = "GAMA MEDIA";
        resaltarGama = true;
      }
    }

    // Temporalidad
    const temporalidad = verMensual ? "CADA MES" : "CADA AÑO";

    return { tipoBase, tipoSufijo, resaltarTipo, gama, resaltarGama, temporalidad };
  };

  // Cargar edificios y datos IPSOS
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);

        const [edificiosData, ipsosRaw] = await Promise.all([
          getEdificiosOperativos({ pais }),
          getIpsosData(pais, "automotriz"),
        ]);

        const sortedEdificios = edificiosData.sort((a, b) => a.edificio.localeCompare(b.edificio));
        setEdificios(sortedEdificios);

        const initialChecked = {};
        sortedEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);

        const ipsosProcesado = procesarIpsosAutomotriz(ipsosRaw);
        setIpsosData(ipsosProcesado);

        if (ipsosProcesado?.preciosDefault) {
          setPrecioGamaAltaOkm(ipsosProcesado.preciosDefault.gamaAlta.okm);
          setPrecioGamaAltaUsado(ipsosProcesado.preciosDefault.gamaAlta.usado);
          setPrecioGamaMediaOkm(ipsosProcesado.preciosDefault.gamaMedia.okm);
          setPrecioGamaMediaUsado(ipsosProcesado.preciosDefault.gamaMedia.usado);
        }
      } catch (error) {
        console.error("Error al inicializar:", error);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalEstacionamientos, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let estac = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, estacionamientos, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        estac += estacionamientos || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalEstacionamientos: estac,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!ipsosData || !totalEjecutivos) return null;

    const precios = {
      gamaAlta: { okm: precioGamaAltaOkm, usado: precioGamaAltaUsado },
      gamaMedia: { okm: precioGamaMediaOkm, usado: precioGamaMediaUsado },
    };

    return calcularMercadoAutomotriz(totalEjecutivos, ipsosData, precios);
  }, [totalEjecutivos, ipsosData, precioGamaAltaOkm, precioGamaAltaUsado, precioGamaMediaOkm, precioGamaMediaUsado]);

  // Totales filtrados según checkboxes seleccionados
  const totalesFiltrados = calcularTotalFiltrado(resultados);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.automotrizPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>Mercado Automotriz</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.automotrizContent}>
        {/* Tabla resumen */}
        <div className={styles.tablaResumen}>
          <table className="table table-bordered table-sm">
            <tbody>
              {resultados && (
                <>
                  <tr>
                    <td className={cx("text-start", styles.celdaConEtiqueta)}>
                      Tienen auto
                      <span className={cx(styles.badgeIpsos, styles.etiquetaDerecha)}>
                        {(ipsosData.tieneAuto * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="text-end">{formatNumber(resultados.conAuto)}</td>
                  </tr>
                  <tr>
                    <td className={cx("text-start", styles.celdaConEtiqueta)}>
                      Tienen segundo auto
                      <span className={cx(styles.badgeIpsos, styles.etiquetaDerecha)}>
                        {(ipsosData.tieneSegundoAuto * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="text-end">+ {formatNumber(resultados.segundoAuto)}</td>
                  </tr>
                  <tr className={styles.filaTotales}>
                    <td className="text-start">Autos totales en universo {pais === "Chile" ? "Wecast" : "Visiona"}</td>
                    <td className="text-end">{formatNumber(resultados.autosTotales)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Filtros de análisis */}
        {resultados && (
          <div className={styles.filtrosContainer}>
            <div className={styles.filtrosCheckboxes}>
              <label>
                <input
                  type="checkbox"
                  checked={filtroOkmGamaAlta}
                  onChange={(e) => setFiltroOkmGamaAlta(e.target.checked)}
                />
                0KM Gama Alta
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filtroOkmGamaMedia}
                  onChange={(e) => setFiltroOkmGamaMedia(e.target.checked)}
                />
                0KM Gama Media
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filtroUsadoGamaAlta}
                  onChange={(e) => setFiltroUsadoGamaAlta(e.target.checked)}
                />
                Usados Gama Alta
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filtroUsadoGamaMedia}
                  onChange={(e) => setFiltroUsadoGamaMedia(e.target.checked)}
                />
                Usados Gama Media
              </label>
              <label>
                <input type="checkbox" checked={verMensual} onChange={(e) => setVerMensual(e.target.checked)} />
                Ver mensual
              </label>
            </div>
          </div>
        )}

        {/* Tabla principal de resultados + Resumen ejecutivo */}
        {resultados && (
          <div className={styles.tablaResultadosContainer}>
            <table className={cx("table table-bordered table-sm", styles.tablaResultados)}>
              <colgroup>
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
              </colgroup>
              <tbody>
                <tr>
                  <td className="text-start">Frecuencia de compra</td>
                  <td className={cx("text-center", getBlurClass("header", "alta"))}>
                    <span className={styles.badgeIpsos}>8% compra cada 1-2 años</span>
                  </td>
                  <td className={cx("text-center", getBlurClass("header", "media"))}>
                    <span className={styles.badgeIpsos}>79% compra cada 3-9 años</span>
                  </td>
                  {/* Conclusión con rowspan */}
                  <td rowSpan={9} className={styles.celdaConclusion}>
                    {(() => {
                      const { tipoBase, tipoSufijo, resaltarTipo, gama, resaltarGama, temporalidad } =
                        generarTextoResumen();
                      return (
                        <div className={styles.resumenEjecutivo}>
                          <span className={styles.highlight}>
                            {formatNumber(calcularVehiculosFiltrados(resultados))}
                          </span>
                          <br />
                          {tipoBase}
                          {tipoSufijo && (
                            <>
                              {" "}
                              {resaltarTipo ? (
                                <span className={styles.highlightSecondary}>{tipoSufijo}</span>
                              ) : (
                                tipoSufijo
                              )}
                            </>
                          )}
                          {gama && (
                            <>
                              <br />
                              DE {resaltarGama ? <span className={styles.highlightSecondary}>{gama}</span> : gama}
                            </>
                          )}
                          <br />
                          SE COMERCIALIZAN
                          <br />
                          <strong>{temporalidad}</strong>
                          <br />
                          EN LOS EDIFICIOS
                          <br />
                          SELECCIONADOS.
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Interpretación comercial</td>
                  <td className={cx("text-center", getBlurClass("header", "alta"))}>Gama Alta</td>
                  <td className={cx("text-center", getBlurClass("header", "media"))}>Gama Media</td>
                </tr>
                <tr>
                  <td className="text-start">Unidades 0KM por {verMensual ? "mes" : "año"}</td>
                  <td className={cx("text-center", getBlurClass("okm", "alta"))}>
                    {formatNumber(verMensual ? resultados.gamaAlta.okm.anual / 12 : resultados.gamaAlta.okm.anual)}
                  </td>
                  <td className={cx("text-center", getBlurClass("okm", "media"))}>
                    {formatNumber(verMensual ? resultados.gamaMedia.okm.anual / 12 : resultados.gamaMedia.okm.anual)}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Precio promedio 0KM</td>
                  <td className={cx("text-center", getBlurClass("okm", "alta"))}>
                    <select
                      value={precioGamaAltaOkm}
                      onChange={(e) => setPrecioGamaAltaOkm(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_ALTA.okm.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={cx("text-center", getBlurClass("okm", "media"))}>
                    <select
                      value={precioGamaMediaOkm}
                      onChange={(e) => setPrecioGamaMediaOkm(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_MEDIA.okm.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Mercado 0KM {verMensual ? "mensual" : "anual"}</td>
                  <td className={cx("text-center", getBlurClass("okm", "alta"))}>
                    {formatUSD(verMensual ? resultados.mercado.gamaAlta.okm / 12 : resultados.mercado.gamaAlta.okm)}
                  </td>
                  <td className={cx("text-center", getBlurClass("okm", "media"))}>
                    {formatUSD(verMensual ? resultados.mercado.gamaMedia.okm / 12 : resultados.mercado.gamaMedia.okm)}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Unidades usados por {verMensual ? "mes" : "año"}</td>
                  <td className={cx("text-center", getBlurClass("usado", "alta"))}>
                    {formatNumber(verMensual ? resultados.gamaAlta.usado.anual / 12 : resultados.gamaAlta.usado.anual)}
                  </td>
                  <td className={cx("text-center", getBlurClass("usado", "media"))}>
                    {formatNumber(
                      verMensual ? resultados.gamaMedia.usado.anual / 12 : resultados.gamaMedia.usado.anual
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Precio promedio usado</td>
                  <td className={cx("text-center", getBlurClass("usado", "alta"))}>
                    <select
                      value={precioGamaAltaUsado}
                      onChange={(e) => setPrecioGamaAltaUsado(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_ALTA.usado.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={cx("text-center", getBlurClass("usado", "media"))}>
                    <select
                      value={precioGamaMediaUsado}
                      onChange={(e) => setPrecioGamaMediaUsado(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_MEDIA.usado.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Mercado usado {verMensual ? "mensual" : "anual"}</td>
                  <td className={cx("text-center", getBlurClass("usado", "alta"))}>
                    {formatUSD(verMensual ? resultados.mercado.gamaAlta.usado / 12 : resultados.mercado.gamaAlta.usado)}
                  </td>
                  <td className={cx("text-center", getBlurClass("usado", "media"))}>
                    {formatUSD(
                      verMensual ? resultados.mercado.gamaMedia.usado / 12 : resultados.mercado.gamaMedia.usado
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Subtotal por gama</td>
                  <td className={cx("text-center", getBlurClass("header", "alta"))}>
                    {formatUSD(verMensual ? totalesFiltrados.gamaAlta / 12 : totalesFiltrados.gamaAlta)}
                  </td>
                  <td className={cx("text-center", getBlurClass("header", "media"))}>
                    {formatUSD(verMensual ? totalesFiltrados.gamaMedia / 12 : totalesFiltrados.gamaMedia)}
                  </td>
                </tr>
                <tr className={styles.filaTotales}>
                  <td className="text-start">Mercado total {verMensual ? "mensual" : "anual"}</td>
                  <td className="text-center" colSpan={3}>
                    {formatUSD(verMensual ? totalesFiltrados.total / 12 : totalesFiltrados.total)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Disclaimer / Letra chica */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Y a este universo y mercado, debemos agregar:</strong>
              </p>
              <p>• Visitantes que tienen autos.</p>
              <p>
                • Ejecutivos que trabajan en los edificios y tienen autos, pero no acceden a un lugar de
                estacionamiento.
              </p>
              <p>
                • Oportunidades en el segmento B2B en edificios con empresas vinculadas a sectores con flotas de
                vehículos: Minería, Logística, Retail, Infraestructura, Renting, entre otros.
                <a href="/listado-empresas" target="_blank" rel="noopener noreferrer">
                  → Revisar empresas por edificio
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Seleccionar Inventario</h3>
              <button className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <span>
                  <strong>{formatNumber(totalEstacionamientos)}</strong> estacionamientos
                </span>
                <button className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    <th scope="col" className="text-center" style={{ width: "50px" }}>
                      {checkAll ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                    </th>
                    <th scope="col" className="text-start">
                      EDIFICIO
                    </th>
                    <th scope="col" className="text-center">
                      UBICACIÓN
                    </th>
                    <th scope="col" className="text-center">
                      EJECUTIVOS
                    </th>
                    <th scope="col" className="text-center">
                      PARKINGS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ created_at, edificio, id, ubicacion, personas, estacionamientos }) => (
                    <tr
                      key={created_at}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-start">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                      <td className="text-center">{estacionamientos ? formatNumber(estacionamientos) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Aplicar selección
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Automotriz;
